import type { Role, UserKpis } from "../../lib/admin/types";
import { AdminAvatar } from "./AdminAvatar";
import { RolePill } from "./RolePill";

export type UserDetailHeaderProps = {
  displayName: string;
  identifier: string;
  /** ISO string from the API. */
  createdAt: string;
  roles: Role[];
  kpis: UserKpis;
  /** User id — seeds the deterministic avatar colour. */
  userId?: number | string;
};

function pct(v: number | null): string {
  return v === null ? "—" : `${Math.round(v * 100)}%`;
}

function deltaCopy(d: number | null): { text: string; tone: "up" | "down" | "flat" | "none" } {
  if (d === null) return { text: "—", tone: "none" };
  const pts = Math.round(d * 100);
  if (pts === 0) return { text: "vlak", tone: "flat" };
  if (pts > 0) return { text: `+${pts} punten`, tone: "up" };
  return { text: `${pts} punten`, tone: "down" };
}

export function UserDetailHeader({
  displayName,
  identifier,
  createdAt,
  roles,
  kpis,
  userId,
}: UserDetailHeaderProps) {
  const memberSince = new Date(createdAt).toLocaleDateString("nl-NL", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const delta = deltaCopy(kpis.trendDelta);
  const deltaClass =
    delta.tone === "up"
      ? "text-[var(--pass)]"
      : delta.tone === "down"
        ? "text-[var(--fail)]"
        : delta.tone === "flat"
          ? "text-[var(--ink-muted)]"
          : "text-[var(--ink-faint)]";

  return (
    <header className="space-y-6">
      <div className="flex items-center gap-4">
        <AdminAvatar name={displayName} id={userId} size="lg" />
        <div className="space-y-2 min-w-0">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-2">
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">{displayName}</h1>
            <div className="flex flex-wrap gap-1.5">
              {roles.map((r) => (
                <RolePill key={r} role={r} />
              ))}
            </div>
          </div>
          <p className="text-xs uppercase tracking-wider text-[var(--ink-faint)] font-mono-badge">
            {identifier} · lid sinds {memberSince}
          </p>
        </div>
      </div>

      <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-4">
        <Kpi
          label="Sessies"
          value={String(kpis.sessionCount)}
          sub={
            kpis.finishedCount === kpis.sessionCount
              ? null
              : `${kpis.finishedCount} voltooid`
          }
        />
        <Kpi label="Gemiddelde" value={pct(kpis.averagePercentage)} sub={null} />
        <Kpi label="Geslaagd" value={pct(kpis.passRate)} sub={null} />
        <Kpi
          label="Trend"
          value={delta.text}
          valueClassName={deltaClass}
          sub={kpis.trendDelta === null ? "te weinig data" : "laatste vs. ervoor"}
        />
      </dl>
    </header>
  );
}

function Kpi({
  label,
  value,
  sub,
  valueClassName,
}: {
  label: string;
  value: string;
  sub: string | null;
  valueClassName?: string;
}) {
  return (
    <div className="space-y-0.5">
      <dt className="text-xs uppercase tracking-wider text-[var(--ink-faint)] font-mono-badge">
        {label}
      </dt>
      <dd
        className={[
          "text-xl sm:text-2xl font-semibold tracking-tight tabular-nums",
          valueClassName,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {value}
      </dd>
      {sub ? <p className="text-xs text-[var(--ink-faint)]">{sub}</p> : null}
    </div>
  );
}
